<template>
  <div>
    <b-tabs content-class="mt-3">
      <b-tab :title="$t('audit-control.test.details')" active>
        <!-- Existing form content continues here... -->
        <!-- I will wrap the entire existing form content (rows) into this tab -->
        <div class="row">
          <div v-if="form._id" class="col-md-6">
            <b-form-group :label="$t('audit-control.test.no')" v-if="form.no">
              <b-form-input v-model="form.no" disabled></b-form-input>
            </b-form-group>
          </div>

          <div class="col-md-6">
            <b-form-group :label="$t('audit-control.test.status.text')">
              <b-form-select v-model="form.status" :options="statusOptions"></b-form-select>
            </b-form-group>
          </div>

          <!-- Audit Selection for AUDIT_ACTIVITY controls (Only visible during creation) -->
          <div v-if="requiresAuditClass && !form._id" class="col-md-6">
            <b-form-group>
              <label for="">{{ $t("audit-control.audit.text-2") }} <span class="text-danger">*</span></label>
              <b-form-select v-model="form.audit" @change="loadAuditDates">
                <template #first>
                  <b-form-select-option :value="null" disabled>-- {{ $t("common.select") }} --</b-form-select-option>
                </template>
                <b-form-select-option v-for="a in relatedAudits" :key="a._id" :value="a._id"> {{ a.no }} - {{ a.name | translate }} </b-form-select-option>
              </b-form-select>
              <small class="text-muted mt-1 d-block">
                {{ $t("audit-control.audit.selection-info") }}
              </small>
            </b-form-group>
          </div>
        </div>

        <!-- Test Period Selection -->
        <div class="card bg-light p-3 mb-4 border">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="font-weight-bold mb-0"><i class="bx bx-calendar mr-2"></i>Test Dönemi</h6>
            <div v-if="form.testPeriod && !showPeriodEditor">
              <b-button size="sm" variant="outline-primary" @click="showPeriodEditor = true">
                <i class="bx bx-edit"></i>
              </b-button>
            </div>
          </div>

          <div v-if="!form.testPeriod || showPeriodEditor">
            <p class="text-muted small mb-3">
              Aşağıda seçeceğiniz tarih aralığı otomatik olarak "Test Periyodu" alanını dolduracaktır
            </p>
            <div class="row">
              <div class="col-md-4">
                <b-form-group :label="$t('audit-control.test.start-date')">
                  <b-form-datepicker v-model="testPeriodStart" class="mb-2"></b-form-datepicker>
                </b-form-group>
              </div>
              <div class="col-md-4">
                <b-form-group :label="$t('audit-control.test.completion-date')">
                  <b-form-datepicker v-model="testPeriodEnd" class="mb-2"></b-form-datepicker>
                </b-form-group>
              </div>
              <div class="col-md-4">
                <b-form-group :label="$t('audit-control.test.period')">
                  <b-form-input :value="formatPeriodString(form.testPeriod)" disabled readonly></b-form-input>
                </b-form-group>
              </div>
            </div>
            <div v-if="testPeriodStart && testPeriodEnd" class="alert alert-info mt-2 mb-0 small">
              <i class="bx bx-check-circle mr-2"></i><strong>Hesaplanan:</strong> {{ formatDate(testPeriodStart) }} - {{ formatDate(testPeriodEnd) }}
            </div>
          </div>

          <div v-else class="alert alert-info mb-0">
            <strong>{{ $t("audit-control.test.period") }}:</strong> {{ formatPeriodString(form.testPeriod) }}
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <b-form-group :label="$t('audit-control.test.method.text')">
              <b-form-select v-model="form.testMethod" :options="testMethodOptions"></b-form-select>
            </b-form-group>
          </div>

          <div class="col-md-6">
            <b-form-group :label="$t('audit-control.test.sampling-method.text')">
              <b-form-select v-model="form.samplingMethod" :options="samplingMethodOptions"></b-form-select>
            </b-form-group>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <b-form-group>
              <template #label>{{ $t("audit-control.test.population") }} <span class="text-danger">*</span></template>
              <sweet-input :disableValidation="true" v-model="form.population"></sweet-input>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group>
              <template #label>{{ $t("audit-control.test.sample-size") }} <span class="text-danger">*</span></template>
              <sweet-input :disableValidation="true" v-model="form.sampleSize"></sweet-input>
            </b-form-group>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <b-form-group :label="$t('audit-control.test.performed-by')">
              <organization-member-select :selected="form.performedBy" @onSelect="onMemberSelect" :organization="currentOrganization" />
              <div v-if="form.performedBy" class="mt-2">
                <i class="bx bx-trash text-danger hand" @click="form.performedBy = null"></i>
              </div>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group>
              <template #label>{{ $t("audit-control.test.interviewee") }} <span class="text-danger">*</span></template>
              <b-form-input v-model="form.interviewee"></b-form-input>
            </b-form-group>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <b-form-group>
              <template #label>{{ $t("audit-control.test.start-date") }} <span class="text-danger">*</span></template>
              <b-form-datepicker v-model="form.startDate" class="mb-2"></b-form-datepicker>
            </b-form-group>
          </div>
          <div class="col-md-6" v-if="form.status === 'COMPLETED'">
            <b-form-group>
              <template #label>{{ $t("audit-control.test.completion-date") }} <span class="text-danger">*</span></template>
              <b-form-datepicker v-model="form.completionDate" class="mb-2"></b-form-datepicker>
            </b-form-group>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <b-form-group :label="$t('audit-control.test.type')">
              <sweet-input :disableValidation="true" v-model="form.testType"></sweet-input>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group :label="$t('audit-control.test.result.text')">
              <b-form-select v-model="form.result" :options="resultOptions"></b-form-select>
            </b-form-group>
          </div>
        </div>

        <b-form-group>
          <template #label>{{ $t("audit-control.test.note") }} <span class="text-danger">*</span></template>
          <b-form-textarea v-model="form.note" rows="3"></b-form-textarea>
        </b-form-group>

        <div v-if="form._id" class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="m-0">{{ $t("audit-control.finding.text") }}</h5>
            <b-button variant="outline-primary" size="sm" @click="$emit('open-finding', form._id)" v-if="!test || test.status !== 'COMPLETED'">
              <i class="bx bx-plus"></i> {{ $t("common.add") }}
            </b-button>
          </div>
          <table class="table table-bordered table-striped table-sm" v-if="findingsPagination">
            <thead>
              <tr>
                <th>{{ $t("audit-control.finding.no") }}</th>
                <th>{{ $t("audit-control.finding.name") }}</th>
                <th style="width: 50px;"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="finding in findingsPagination.docs" :key="finding._id">
                <td>{{ finding.no }}</td>
                <td>{{ finding.name | translate }}</td>
                <td>
                  <i :class="!test || test.status !== 'COMPLETED' ? 'bx bx-edit text-primary' : 'bx bx-show text-info'" class="hand mr-2" @click="editFinding(finding)"></i>
                  <i v-if="!test || test.status !== 'COMPLETED'" class="bx bx-trash hand text-danger" @click="$emit('delete-finding', finding._id)"></i>
                </td>
              </tr>
              <tr v-if="findingsPagination.totalDocs === 0">
                <td colspan="3" class="text-center">{{ $t("common.no-data") }}</td>
              </tr>
            </tbody>
          </table>
          <div class="row" v-if="findingsPagination">
            <div class="col">
              <b-pagination
                v-model="findingsRequest.options.page"
                :total-rows="findingsPagination.totalDocs"
                :per-page="findingsPagination.limit"
                align="center"
                @change="fetchFindings"
              ></b-pagination>
            </div>
          </div>
        </div>
        <div v-else class="alert alert-warning">
          {{ $t("audit-control.test.save-to-add-finding") }}
        </div>

        <!-- Files Section -->
        <EntityFileSection
          v-if="form._id"
          entityType="controlTest"
          :entityId="form._id"
          :files="form.files || []"
          :canUpload="!test || test.status !== 'COMPLETED'"
          uploadBaseUrl="audit-control/control-test"
          @uploaded="fetchTestDetails(test)"
        />

        <div class="text-right">
          <b-button variant="secondary" @click="$emit('cancel')" class="mr-2">{{ $t("common.cancel") }}</b-button>
          <b-button variant="primary" @click="save" v-if="!test || test.status !== 'COMPLETED'">{{ $t("common.save") }}</b-button>
        </div>
      </b-tab>

      <b-tab :title="$t('audit-control.test.history')" v-if="form._id">
        <OrganizationControlTestHistory :history="form.history" />
      </b-tab>
    </b-tabs>

    <b-modal id="completion-date-modal" :title="$t('audit-control.test.completion-date')" @ok="handleCompletionDateOk" @hidden="handleCompletionDateHidden">
      <b-form-group>
        <template #label>{{ $t("audit-control.test.completion-date") }} <span class="text-danger">*</span></template>
        <b-form-datepicker v-model="tempCompletionDate" class="mb-2" :placeholder="$t('common.select')"></b-form-datepicker>
      </b-form-group>
      <template #modal-cancel>
        {{ $t("common.cancel") }}
      </template>
      <template #modal-ok>
        {{ $t("common.ok") || "Tamam" }}
      </template>
    </b-modal>
  </div>
</template>

<script>
import ModalSelect from "@c/modal-select";
import OrganizationMemberSelect from "@cc/organization-member-select";
import OrganizationControlTestHistory from "./organization-control-test-history";
import EntityFileSection from "@c/entity-file-section.vue";

export default {
  components: { ModalSelect, OrganizationMemberSelect, OrganizationControlTestHistory, EntityFileSection },
  props: {
    controlId: String,
    test: Object, // If editing
  },
  data() {
    return {
      form: {
        status: "ACTIVE",
        startDate: new Date().toISOString().slice(0, 10),
        result: "EFFECTIVE",
      },
      tempCompletionDate: null,
      previousStatus: null,
      testPeriodStart: null,
      testPeriodEnd: null,
      audit: null,
      showPeriodEditor: false,
      relatedAudits: [],
      requiresAuditClass: false,
      statusOptions: [
        { value: "ACTIVE", text: this.$t("audit-control.test.status.active") },
        { value: "PARTIALLY_COMPLETED", text: this.$t("audit-control.test.status.partially_completed") },
        { value: "COMPLETED", text: this.$t("audit-control.test.status.completed") },
      ],
      resultOptions: [
        { value: "EFFECTIVE", text: "Etkin" },
        { value: "NOT_EFFECTIVE", text: "Etkin Değil" },
      ],
      testMethodOptions: [
        { value: "DOCUMENT_REVIEW", text: this.$t("audit-control.test.method.document_review") },
        { value: "REPERFORMANCE", text: this.$t("audit-control.test.method.reperformance") },
        { value: "OBSERVATION", text: this.$t("audit-control.test.method.observation") },
        { value: "INQUIRY", text: this.$t("audit-control.test.method.inquiry") },
        { value: "SAMPLING", text: this.$t("audit-control.test.method.sampling") },
        { value: "OTHER", text: this.$t("audit-control.test.method.other") },
      ],
      samplingMethodOptions: [
        { value: "RANDOM", text: this.$t("audit-control.test.sampling-method.random") },
        { value: "RISK_BASED", text: this.$t("audit-control.test.sampling-method.risk_based") },
      ],
      findingsRequest: {
        options: { page: 1, limit: 5 },
        query: {},
      },
    };
  },
  computed: {
    findingsPagination() {
      return this.$store.getters["organizationFinding/paginationForSearch"];
    },
    control() {
      return this.$store.getters["organizationControl/selectedEntity"];
    },
  },
  watch: {
    test: {
      immediate: true,
      async handler(val) {
        if (val) {
          this.form = this.prepareFormForEdit(val, ["testType", "note", "population", "sampleSize"]);

          this.form = this.prepareFormForEdit(val, ["testType", "note", "population", "sampleSize"]);

          if (!this.form.status && !this.form._id) {
            this.$set(this.form, "status", "ACTIVE");
            this.$set(this.form, "startDate", new Date().toISOString().slice(0, 10));
          }

          if (!this.form.audit) {
            await this.handleIfAuditActivity();
          }

          // Load audit dates if audit exists
          if (this.form.audit) {
            await this.loadAuditDates();
          }
          this.showPeriodEditor = false;
          this.fetchFindings();
        } else {
          this.form = {
            status: "ACTIVE",
            result: "EFFECTIVE",
            startDate: new Date().toISOString().slice(0, 10),
            organization: this.$store.getters["user/organization"],
            control: this.controlId,
            population: {},
            sampleSize: {},
          };
          await this.handleIfAuditActivity();

          // If audit prop exists, load its dates
          if (this.test?.audit) {
            this.form.audit = this.test.audit;
            await this.loadAuditDates();
          }

          this.showPeriodEditor = true;
        }
      },
    },
    testPeriodStart(v) {
      if (v && this.testPeriodEnd) {
        this.updateTestPeriodString();
      }
    },
    testPeriodEnd(v) {
      if (v && this.testPeriodStart) {
        this.updateTestPeriodString();
      }
    },
    "form.status"(val, oldVal) {
      if (val === "COMPLETED" && oldVal && oldVal !== "COMPLETED") {
        this.previousStatus = oldVal;
        this.tempCompletionDate = new Date().toISOString().slice(0, 10);
        this.$bvModal.show("completion-date-modal");
      } else if (val !== "COMPLETED") {
        this.$set(this.form, "completionDate", null);
      }
    },
  },
  methods: {
    async fetchTestDetails(test) {
      try {
        await this.$store.dispatch("organizationControlTest/findById", test._id);
        this.test = this.$store.getters["organizationControlTest/selectedEntity"];
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async handleIfAuditActivity() {
      // Check if control is AUDIT_ACTIVITY and needs an audit
      try {
        const control = this.$store.getters["organizationControl/selectedEntity"];
        this.requiresAuditClass = control && control.class === "AUDIT_ACTIVITY";

        if (this.requiresAuditClass && !this.form.audit) {
          const req = {
            query: { controls: { value: this.controlId, isObject: true }, status: { value: "IN_PROGRESS", isObject: true } },
            options: { limit: 100 },
          };

          const res = await this.$store.dispatch("organizationAudit/paginate", req);
          this.relatedAudits = res.data.docs || [];
        }
      } catch (e) {}
    },

    editFinding(finding) {
      // this.$store.commit("organizationFinding/editedEntity", finding);
      // this.$emit("open-finding", this.form._id, finding);
      if (finding) {
        return this.$router.push(`/finding/detail/${finding._id}`);
      }
    },
    async fetchFindings() {
      if (!this.form._id) return;
      this.findingsRequest.query.test = { value: this.form._id, isObject: true };
      await this.$store.dispatch("organizationFinding/paginateForSearch", this.findingsRequest);
    },
    async loadAuditDates() {
      try {
        const auditId = this.form.audit?._id || this.form.audit;
        if (!auditId) return;

        const audit = await this.$store.dispatch("organizationAudit/findById", auditId);
        if (audit) {
          const auditData = this.$store.getters["organizationAudit/selectedEntity"];
          if (auditData?.startDate && auditData?.endDate) {
            this.testPeriodStart = auditData.startDate;
            this.testPeriodEnd = auditData.endDate;
            this.updateTestPeriodString();
          }
        }
      } catch (e) {}
    },
    updateTestPeriodString() {
      if (this.testPeriodStart && this.testPeriodEnd) {
        const startStr = typeof this.testPeriodStart === "string" ? this.testPeriodStart : this.formatDate(this.testPeriodStart);
        const endStr = typeof this.testPeriodEnd === "string" ? this.testPeriodEnd : this.formatDate(this.testPeriodEnd);
        this.form.testPeriod = `${startStr} - ${endStr}`;
      }
    },
    formatDate(date) {
      if (!date) return "";
      let d;

      if (typeof date === "string") {
        // Parse ISO string or regular date string
        d = new Date(date);
      } else {
        d = new Date(date);
      }

      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${d.getFullYear()}-${month}-${day}`;
    },
    formatPeriodString(periodStr) {
      if (!periodStr) return "";

      const parts = periodStr.split(" - ");
      if (parts.length === 2) {
        const start = this.formatDate(parts[0].trim());
        const end = this.formatDate(parts[1].trim());
        return `${start} - ${end}`;
      }
      return periodStr;
    },
    onMemberSelect(item) {
      this.form = { ...this.form, performedBy: item };
    },
    async save() {
      // Basic validation
      if (
        !this.form.startDate ||
        (this.form.status === "COMPLETED" && !this.form.completionDate) ||
        !this.form.population ||
        !this.form.sampleSize ||
        !this.form.interviewee ||
        !this.form.note ||
        (this.control.class === "AUDIT_ACTIVITY" && !this.form.audit)
      ) {
        this.$toast.warning(this.$t("common.validation-error"));
        return;
      }

      if (this.requiresAuditClass && !this.form?._id && !this.form.audit) {
        this.$toast.warning("Lütfen bu testin ilgili olduğu denetimi seçiniz.");
        return;
      }

      this.$emit("save", this.form);
    },
    handleCompletionDateOk(bvModalEvent) {
      if (!this.tempCompletionDate) {
        bvModalEvent.preventDefault();
        this.$toast.warning(this.$t("task.required-field-warning"));
      } else {
        this.$set(this.form, "completionDate", this.tempCompletionDate);
      }
    },
    handleCompletionDateHidden() {
      if (!this.form.completionDate && this.form.status === "COMPLETED") {
        this.form.status = this.previousStatus || "ACTIVE";
      }
    },
  },
};
</script>
